/**
 * Meeting session: the state machine behind the UI. Pure logic + a narrow client interface so the
 * whole flow is testable with a fake transport (no browser, no SFU).
 *
 * Scenario additions (1v1 / live / voice) ride on the same session so media, token renewal and the
 * data channel stay in one place:
 *  - `scene` selects the layout; `maxParticipants` enforces the two-party cap;
 *  - live comments + reactions use the `live` data topic;
 *  - host moderation intents (mute/kick/role, voice approval) use the `meeting` data topic and are
 *    applied cooperatively by the target (the token remains the publish authority);
 *  - system messages carry an i18n key + params instead of English text.
 */
import type { RtcError, RtcEventName } from '@nimeet/rtc-web';
import type { MessageKey } from '../i18n/messages';
import {
  clearStoredChatHistory,
  defaultHistoryStorage,
  loadChatHistory,
  saveChatHistory,
  type HistoryStorage,
} from './history';
import type { Translate, TranslateParams } from '../i18n';
import type { SceneId } from '../scenarios/scenes';
import type { LiveReactionKind } from '../scenarios/machines';

export type MeetingPhase = 'idle' | 'joining' | 'joined' | 'reconnecting' | 'left' | 'failed';
export type MeetingRole = 'host' | 'audience';

export interface MeetingParticipant {
  uid: number;
  userId: string | null;
  displayName: string;
  role: MeetingRole;
  audioMuted: boolean;
  videoMuted: boolean;
  isLocal: boolean;
  speaking: boolean;
  networkQuality: number | null;
  handRaised: boolean;
  /** Voice room: uid currently holds a mic seat (host or approved listener). */
  seated: boolean;
}

export interface ChatMessage {
  id: string;
  from: number;
  fromName: string;
  text: string;
  at: string;
  kind: 'user' | 'system' | 'reaction';
  /** Localized system copy; when set, `text` is empty and the UI renders `t(textKey, params)`. */
  textKey?: MessageKey;
  /** True for a message restored from this browser's local history (round-trip marker). */
  restored?: boolean;
  params?: TranslateParams;
}

export interface JoinOptions {
  channel: string;
  displayName: string;
  role: MeetingRole;
  /**
   * Role to request from the token service when it differs from the app-level role. Voice listeners
   * join as `audience` (not publishing) but need a publish-capable token so the host can approve
   * them onto a mic seat later; live audience never gets one (token-enforced subscribe-only).
   */
  tokenRole?: MeetingRole;
  uid?: number;
  publishAudio: boolean;
  publishVideo: boolean;
  scene?: SceneId;
  /** Two-party scenes reject the join once the room is already at capacity. */
  maxParticipants?: number | null;
}

export interface SessionState {
  phase: MeetingPhase;
  scene: SceneId;
  channel: string | null;
  localUid: number | null;
  participants: MeetingParticipant[];
  messages: ChatMessage[];
  /** Number of messages restored from this browser's local chat history for the current channel. */
  historyRestored: number;
  liveComments: ChatMessage[];
  reactions: Record<LiveReactionKind, number>;
  lastReaction: { kind: LiveReactionKind; from: number; at: string } | null;
  unread: number;
  unreadLive: number;
  chatOpen: boolean;
  boardOpen: boolean;
  screenSharing: boolean;
  micEnabled: boolean;
  cameraEnabled: boolean;
  locked: boolean;
  error: string | null;
  /** Localized error key; preferred over `error` when present. */
  errorKey: MessageKey | null;
  /** One-shot localized notice for a toast (host muted you, approved your mic, ...). */
  noticeKey: MessageKey | null;
  tokenExpiresAt: number | null;
  renewing: boolean;
  stats: { rttMs: number; bitrateKbps: number; fps: number } | null;
}

/** Narrow surface of RtcClient used by the meeting; keeps the session unit-testable. */
export interface MeetingClient {
  joinChannel(channel: string, options?: Record<string, unknown>): Promise<void>;
  leaveChannel(): Promise<void>;
  renewToken(): Promise<void>;
  muteAudio(muted: boolean): Promise<void>;
  muteVideo(muted: boolean): Promise<void>;
  startScreenShare(): Promise<void>;
  stopScreenShare(): Promise<void>;
  sendData(message: { payload: Uint8Array; reliable?: boolean; topic?: string }): Promise<void>;
  getStats(): Promise<{ rttMs: number; bitrateKbps: number; framesPerSecond: number }>;
  on(event: string, listener: (payload: never) => void): () => void;
  off?(event: string, listener: (payload: never) => void): void;
  /** Optional on the fake client used by unit tests; the real RtcClient implements all three. */
  setRole?(role: MeetingRole): Promise<void>;
  publishAudio?(): Promise<void>;
  publishVideo?(): Promise<void>;
  unpublishAudio?(): Promise<void>;
  unpublishVideo?(): Promise<void>;
}

const MAX_MESSAGES = 300;
const MAX_LIVE_COMMENTS = 200;
const REACTIONS: readonly LiveReactionKind[] = ['heart', 'clap', 'like'];

export class MeetingSession {
  private state: SessionState = {
    phase: 'idle',
    scene: 'meeting',
    channel: null,
    localUid: null,
    participants: [],
    messages: [],
    historyRestored: 0,
    liveComments: [],
    reactions: { heart: 0, clap: 0, like: 0 },
    lastReaction: null,
    unread: 0,
    unreadLive: 0,
    chatOpen: false,
    boardOpen: false,
    screenSharing: false,
    micEnabled: true,
    cameraEnabled: true,
    locked: false,
    error: null,
    errorKey: null,
    noticeKey: null,
    tokenExpiresAt: null,
    renewing: false,
    stats: null,
  };

  private readonly listeners = new Set<(state: SessionState) => void>();
  private readonly unsubscribers: Array<() => void> = [];
  private readonly names = new Map<number, string>();
  /**
   * Presence messages and the media-edge `user-joined` event race: the data channel can deliver a
   * peer's `presence` before the transport reports that peer as joined, so patching the roster at
   * that moment is a no-op and the newcomer would keep the "User <uid>" placeholder forever. Cache
   * the announced role/name by uid and apply it when the participant is actually created.
   */
  private readonly announcedPresence = new Map<
    number,
    { role: MeetingRole; displayName: string | null }
  >();
  /**
   * The first presence broadcast can be emitted before the SFU has fully wired this participant's
   * data channel (observable for the last person joining a large room: everyone else sees the
   * "User <uid>" placeholder forever). Repeat it once shortly after join.
   */
  private presenceRetry: ReturnType<typeof setTimeout> | null = null;
  private attached = false;

  /** Storage used to persist/restore the local chat history; `null` disables persistence. */
  private readonly historyStorage: HistoryStorage | null;
  /** The history loaded for the current channel, kept so a re-render cannot lose it. */
  private restoredHistory: ChatMessage[] = [];

  constructor(
    private readonly client: MeetingClient,
    private readonly options: {
      now?: () => number;
      sendChat?: (text: string) => void;
      t?: Translate;
      /** Injectable storage (tests); defaults to this browser's `localStorage`. */
      historyStorage?: HistoryStorage | null;
    } = {},
  ) {
    this.historyStorage =
      options.historyStorage === undefined ? defaultHistoryStorage() : options.historyStorage;
  }

  getState(): SessionState {
    return this.state;
  }

  subscribe(listener: (state: SessionState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private now(): number {
    return this.options.now?.() ?? Date.now();
  }

  /** Fallback display copy for a uid we have no name for; localized when a translator is injected. */
  private guestName(uid: number): string {
    return this.options.t ? this.options.t('system.guest', { uid }) : `User ${uid}`;
  }

  private set(patch: Partial<SessionState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener(this.state);
  }

  /** Wire client events exactly once. */
  attach(): void {
    if (this.attached) return;
    this.attached = true;
    const on = this.client.on.bind(this.client);
    this.unsubscribers.push(
      on('connection-state-changed', (event: { current: string; reason?: string }) => {
        if (event.current === 'connected')
          this.set({ phase: 'joined', error: null, errorKey: null });
        else if (event.current === 'reconnecting') this.set({ phase: 'reconnecting' });
        else if (event.current === 'failed')
          this.set({ phase: 'failed', error: event.reason ?? 'connection failed' });
        else if (event.current === 'disconnected' && this.state.phase !== 'idle')
          this.set({ phase: 'left' });
      }),
      on('user-joined', (event: { uid: number; userId?: string | null }) => {
        const announced = this.announcedPresence.get(event.uid);
        this.upsertParticipant({
          uid: event.uid,
          userId: event.userId ?? null,
          // Empty until the presence message arrives: the "X joined" system line is emitted then, so
          // the chat never shows a numeric placeholder for a real person. If presence arrived early
          // (see `announcedPresence`), the real name/role are already known here.
          displayName: announced?.displayName ?? this.names.get(event.uid) ?? '',
          role: announced?.role ?? 'audience',
          audioMuted: false,
          videoMuted: false,
          isLocal: false,
          speaking: false,
          networkQuality: null,
          handRaised: false,
          seated: false,
        });
        // The media-edge `user-joined` payload carries no app role, so peers exchange a presence
        // message over the data channel; the newcomer learns everyone's role and vice versa.
        void this.announcePresence().catch(() => undefined);
      }),
      on('user-left', (event: { uid: number }) => {
        this.announcedPresence.delete(event.uid);
        const participant = this.participant(event.uid);
        this.set({
          participants: this.state.participants.filter((entry) => entry.uid !== event.uid),
          messages: participant
            ? [
                ...this.state.messages,
                systemMessage('system.left', { name: participant.displayName }, this.now()),
              ].slice(-MAX_MESSAGES)
            : this.state.messages,
        });
      }),
      on(
        'user-mute-changed',
        (event: { uid: number; mediaType: 'audio' | 'video'; muted: boolean }) => {
          this.patchParticipant(
            event.uid,
            event.mediaType === 'audio' ? { audioMuted: event.muted } : { videoMuted: event.muted },
          );
        },
      ),
      // Track publication arrives after the participant-joined event. Re-render on it so the video
      // tile and the hidden audio sink can attach the newly subscribed track.
      on('user-published', (event: { uid: number; mediaType: 'audio' | 'video' }) => {
        this.patchParticipant(
          event.uid,
          event.mediaType === 'audio' ? { audioMuted: false } : { videoMuted: false },
        );
      }),
      on('user-unpublished', (event: { uid: number; mediaType: 'audio' | 'video' }) => {
        this.patchParticipant(
          event.uid,
          event.mediaType === 'audio' ? { audioMuted: true } : { videoMuted: true },
        );
      }),
      on('user-role-changed', (event: { uid: number; role: string }) => {
        this.patchParticipant(event.uid, { role: event.role === 'host' ? 'host' : 'audience' });
      }),
      on('volume-indicator', (event: { uid: number; level: number }) => {
        this.patchParticipant(event.uid, { speaking: event.level > 0.05 });
      }),
      on('network-quality', (event: { uid: number; quality: number }) => {
        this.patchParticipant(event.uid, { networkQuality: event.quality });
      }),
      on('data-received', (event: { from: number; payload: Uint8Array; topic?: string }) => {
        this.handleData(event);
      }),
      on('token-privilege-will-expire', () => {
        void this.renew();
      }),
      on('token-privilege-did-expire', () => {
        this.set({
          error: 'token expired; the meeting will be re-joined',
          errorKey: 'toast.tokenExpired',
          phase: 'failed',
        });
      }),
    );
  }

  detach(): void {
    if (this.presenceRetry !== null) {
      clearTimeout(this.presenceRetry);
      this.presenceRetry = null;
    }
    for (const unsubscribe of this.unsubscribers.splice(0)) unsubscribe();
    this.attached = false;
  }

  async join(options: JoinOptions): Promise<void> {
    this.attach();
    this.restoredHistory = loadChatHistory(this.historyStorage, options.channel);
    this.set({
      phase: 'joining',
      scene: options.scene ?? this.state.scene,
      channel: options.channel,
      error: null,
      errorKey: null,
      micEnabled: options.publishAudio,
      cameraEnabled: options.publishVideo,
      messages: [
        ...this.restoredHistory,
        systemMessage('system.joining', { channel: options.channel }, this.now()),
      ],
      historyRestored: this.restoredHistory.length,
      unread: 0,
      liveComments: [],
      unreadLive: 0,
      reactions: { heart: 0, clap: 0, like: 0 },
      lastReaction: null,
    });
    this.names.set(options.uid ?? 0, options.displayName);
    try {
      await this.client.joinChannel(options.channel, {
        ...(options.uid !== undefined ? { uid: options.uid } : {}),
        role: options.tokenRole ?? options.role,
        publishAudio: options.publishAudio,
        publishVideo: options.publishVideo,
      });
      const localUid = options.uid ?? 0;
      // `joinChannel()` can emit `user-joined` for participants already in the room while it is
      // resolving. Keep those entries; replacing the array here made only the first joiner visible
      // to later joiners (the classic "everyone sees me, I see nobody" asymmetry).
      const existingRemote = this.state.participants.filter(
        (participant) => !participant.isLocal && participant.uid !== localUid,
      );
      const maxParticipants = options.maxParticipants ?? null;
      if (maxParticipants !== null && existingRemote.length + 1 > maxParticipants) {
        await this.client.leaveChannel().catch(() => undefined);
        this.set({ phase: 'failed', error: 'room is full', errorKey: '1v1.full' });
        throw new SceneCapacityError('1v1.full');
      }
      this.set({
        phase: 'joined',
        localUid,
        participants: [
          {
            uid: localUid,
            userId: null,
            displayName: options.displayName,
            role: options.role,
            audioMuted: !options.publishAudio,
            videoMuted: !options.publishVideo,
            isLocal: true,
            speaking: false,
            networkQuality: null,
            handRaised: false,
            seated: options.role === 'host',
          },
          ...existingRemote,
        ],
        messages: [
          ...this.restoredHistory,
          systemMessage('system.joined', { channel: options.channel }, this.now()),
        ],
        historyRestored: this.restoredHistory.length,
        unread: 0,
      });
      void this.announcePresence().catch(() => undefined);
      this.schedulePresenceRetry();
    } catch (error) {
      if (error instanceof SceneCapacityError) throw error;
      this.set({ phase: 'failed', error: describeError(error) });
      throw error;
    }
  }

  async leave(): Promise<void> {
    try {
      await this.client.leaveChannel();
    } finally {
      this.detach();
      this.set({ phase: 'left', participants: [], channel: null, localUid: null });
    }
  }

  async toggleMic(): Promise<void> {
    const enabled = !this.state.micEnabled;
    await this.client.muteAudio(!enabled);
    this.set({ micEnabled: enabled });
    this.patchParticipant(this.state.localUid ?? 0, { audioMuted: !enabled });
  }

  async toggleCamera(): Promise<void> {
    const enabled = !this.state.cameraEnabled;
    await this.client.muteVideo(!enabled);
    this.set({ cameraEnabled: enabled });
    this.patchParticipant(this.state.localUid ?? 0, { videoMuted: !enabled });
  }

  async toggleScreenShare(): Promise<void> {
    if (this.state.screenSharing) {
      await this.client.stopScreenShare();
      this.set({ screenSharing: false });
      return;
    }
    await this.client.startScreenShare();
    this.set({ screenSharing: true });
  }

  async renew(): Promise<void> {
    if (this.state.renewing) return;
    this.set({ renewing: true });
    try {
      await this.client.renewToken();
      this.set({ renewing: false, error: null, errorKey: null });
    } catch (error) {
      this.set({ renewing: false, error: describeError(error) });
    }
  }

  openChat(open = true): void {
    this.set({ chatOpen: open, unread: open ? 0 : this.state.unread });
  }

  openBoard(open = true): void {
    this.set({ boardOpen: open });
  }

  async sendChat(text: string): Promise<void> {
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    const uid = this.state.localUid ?? 0;
    const message: ChatMessage = {
      id: `m_${Math.random().toString(36).slice(2, 10)}`,
      from: uid,
      fromName:
        this.participant(uid)?.displayName ??
        (this.options.t ? this.options.t('common.you') : 'You'),
      text: trimmed,
      at: new Date(this.now()).toISOString(),
      kind: 'user',
    };
    await this.client.sendData({
      payload: new TextEncoder().encode(JSON.stringify({ type: 'chat', ...message })),
      topic: 'chat',
    });
    this.appendMessage(message);
  }

  /** Live comments ride the `live` topic so they are never mixed with the meeting chat. */
  async sendLiveComment(text: string): Promise<void> {
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    const uid = this.state.localUid ?? 0;
    const comment: ChatMessage = {
      id: `l_${Math.random().toString(36).slice(2, 10)}`,
      from: uid,
      fromName: this.participant(uid)?.displayName ?? 'You',
      text: trimmed,
      at: new Date(this.now()).toISOString(),
      kind: 'user',
    };
    await this.client.sendData({
      payload: new TextEncoder().encode(JSON.stringify({ type: 'live:comment', ...comment })),
      topic: 'live',
    });
    this.appendLiveComment(comment);
  }

  async sendReaction(kind: LiveReactionKind): Promise<void> {
    if (!REACTIONS.includes(kind)) return;
    const uid = this.state.localUid ?? 0;
    await this.client.sendData({
      payload: new TextEncoder().encode(JSON.stringify({ type: 'live:reaction', kind, uid })),
      topic: 'live',
    });
    this.applyReaction(kind, uid, new Date(this.now()).toISOString());
  }

  async setHandRaised(raised: boolean): Promise<void> {
    this.patchParticipant(this.state.localUid ?? 0, { handRaised: raised });
    await this.sendData({ type: 'hand', uid: this.state.localUid ?? 0, raised });
    // A listener lowering or raising a hand is also mirrored into the voice machine by the UI.
  }

  /**
   * Host moderation intent. The target applies it cooperatively on its next data message; the token
   * remains the only hard publish authority (documented in docs/developer/demo-scenarios.md).
   */
  async hostAction(
    targetUid: number,
    action: 'mute' | 'kick' | 'role',
    role?: MeetingRole,
  ): Promise<void> {
    await this.sendData({ type: 'host-action', targetUid, action, ...(role ? { role } : {}) });
    this.appendMessage(
      systemMessage(
        'system.roleChanged',
        { name: this.names.get(targetUid) ?? targetUid, role: role ?? action },
        this.now(),
      ),
    );
  }

  /** One-shot notice consumed by the room shell and shown as a toast. */
  consumeNotice(): MessageKey | null {
    const key = this.state.noticeKey;
    if (key !== null) this.set({ noticeKey: null });
    return key;
  }

  /** Voice: host approves a raised hand -> the listener is asked onto a seat. */
  async approveMic(targetUid: number): Promise<void> {
    await this.sendData({ type: 'host-action', targetUid, action: 'mic-approved' });
  }

  /** Listener side of an approval: upgrade the local role and start publishing audio. */
  async acceptMicApproval(): Promise<void> {
    if (this.client.setRole) await this.client.setRole('host').catch(() => undefined);
    if (this.client.publishAudio) await this.client.publishAudio().catch(() => undefined);
    this.set({ micEnabled: true });
    this.patchParticipant(this.state.localUid ?? 0, {
      role: 'host',
      audioMuted: false,
      seated: true,
    });
  }

  /** Host moderation. In live mode the kick is also enforced by the control plane API. */
  muteAll(): number {
    let muted = 0;
    for (const participant of this.state.participants) {
      if (!participant.isLocal && !participant.audioMuted) {
        this.patchParticipant(participant.uid, { audioMuted: true });
        muted += 1;
      }
    }
    this.appendMessage(systemMessage('system.hostMuted', { count: muted }, this.now()));
    return muted;
  }

  kick(uid: number): void {
    const target = this.participant(uid);
    if (!target || target.isLocal) return;
    this.set({ participants: this.state.participants.filter((entry) => entry.uid !== uid) });
    this.appendMessage(systemMessage('system.removed', { name: target.displayName }, this.now()));
  }

  setRole(uid: number, role: MeetingRole): void {
    this.patchParticipant(uid, {
      role,
      seated: role === 'host' || this.participant(uid)?.seated === true,
    });
    this.appendMessage(
      systemMessage(
        'system.roleChanged',
        { name: this.participant(uid)?.displayName ?? uid, role },
        this.now(),
      ),
    );
  }

  toggleLock(): boolean {
    const locked = !this.state.locked;
    this.set({ locked });
    return locked;
  }

  /** Voice-room seat bookkeeping (the seat layout itself lives in the pure voice machine). */
  setSeated(uid: number, seated: boolean): void {
    this.patchParticipant(uid, { seated });
  }

  async refreshStats(): Promise<void> {
    const stats = await this.client.getStats();
    this.set({
      stats: { rttMs: stats.rttMs, bitrateKbps: stats.bitrateKbps, fps: stats.framesPerSecond },
    });
  }

  /** Re-broadcast presence once after the data channel has settled (see `presenceRetry`). */
  private schedulePresenceRetry(): void {
    if (this.presenceRetry !== null) clearTimeout(this.presenceRetry);
    this.presenceRetry = setTimeout(() => {
      this.presenceRetry = null;
      if (this.state.phase === 'joined') void this.announcePresence().catch(() => undefined);
    }, 1200);
  }

  /** Broadcast this client's app-level role/name so peers can render host/audience correctly. */
  private async announcePresence(): Promise<void> {
    const uid = this.state.localUid;
    if (uid === null) return;
    const local = this.participant(uid);
    await this.sendData({
      type: 'presence',
      uid,
      displayName: local?.displayName ?? this.names.get(uid) ?? this.guestName(uid),
      role: local?.role ?? 'audience',
    });
  }

  private async sendData(payload: Record<string, unknown>): Promise<void> {
    await this.client.sendData({
      payload: new TextEncoder().encode(JSON.stringify(payload)),
      topic: 'meeting',
    });
  }

  private handleData(event: { from: number; payload: Uint8Array; topic?: string }): void {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(new TextDecoder().decode(event.payload)) as Record<string, unknown>;
    } catch {
      return;
    }
    const topic = event.topic ?? 'meeting';
    if (parsed.type === 'presence') {
      const uid = Number(parsed.uid ?? event.from);
      const role: MeetingRole = parsed.role === 'host' ? 'host' : 'audience';
      const displayName = typeof parsed.displayName === 'string' ? parsed.displayName : undefined;
      // Data can overtake the media-edge join notification; cache it either way.
      this.announcedPresence.set(uid, { role, displayName: displayName ?? null });
      const existing = this.participant(uid);
      if (!existing) return; // `user-joined` will apply this cached presence when it lands
      const unnamed =
        existing.displayName.length === 0 || existing.displayName === this.guestName(uid);
      this.patchParticipant(uid, { role, ...(displayName ? { displayName } : {}) });
      if (unnamed && displayName) {
        this.appendMessage(systemMessage('system.userJoined', { name: displayName }, this.now()));
      }
      return;
    }
    if (parsed.type === 'chat') {
      const message: ChatMessage = {
        id: String(parsed.id ?? `m_${Math.random().toString(36).slice(2, 10)}`),
        from: event.from,
        fromName: String(
          parsed.fromName ?? this.names.get(event.from) ?? this.guestName(event.from),
        ),
        text: String(parsed.text ?? ''),
        at: String(parsed.at ?? new Date(this.now()).toISOString()),
        kind: 'user',
      };
      this.appendMessage(message);
      return;
    }
    if (parsed.type === 'live:comment') {
      this.appendLiveComment({
        id: String(parsed.id ?? `l_${Math.random().toString(36).slice(2, 10)}`),
        from: event.from,
        fromName: String(
          parsed.fromName ?? this.names.get(event.from) ?? this.guestName(event.from),
        ),
        text: String(parsed.text ?? ''),
        at: String(parsed.at ?? new Date(this.now()).toISOString()),
        kind: 'user',
      });
      return;
    }
    if (parsed.type === 'live:reaction') {
      const kind = parsed.kind as LiveReactionKind;
      if (REACTIONS.includes(kind))
        this.applyReaction(kind, event.from, new Date(this.now()).toISOString());
      return;
    }
    if (parsed.type === 'host-action') {
      this.handleHostAction(parsed, event.from);
      return;
    }
    if (parsed.type === 'hand') {
      this.patchParticipant(Number(parsed.uid ?? event.from), {
        handRaised: Boolean(parsed.raised),
      });
      return;
    }
    // Backwards-compatible meeting chat (topic omitted by older clients).
    if (topic === 'chat') return;
  }

  private handleHostAction(payload: Record<string, unknown>, from: number): void {
    const localUid = this.state.localUid;
    const targetUid = Number(payload.targetUid ?? 0);
    const action = String(payload.action ?? '');
    const isLocalTarget = localUid !== null && targetUid === localUid;
    if (action === 'mute' && isLocalTarget) {
      void this.client.muteAudio(true).catch(() => undefined);
      this.set({ micEnabled: false });
      this.patchParticipant(localUid, { audioMuted: true });
      this.set({ noticeKey: 'toast.mutedByHost' });
      return;
    }
    if (action === 'kick' && isLocalTarget) {
      void this.leave().catch(() => undefined);
      this.set({ noticeKey: 'toast.kicked' });
      return;
    }
    if (action === 'mic-approved' && isLocalTarget) {
      // The listener accepts the approval: upgrade the local role and start publishing audio. The
      // token was minted for the audience role, so the media edge may still refuse the publish; the
      // UI surfaces that error instead of pretending it worked.
      void this.acceptMicApproval().catch(() => undefined);
      this.set({ noticeKey: 'toast.approvedMic' });
      return;
    }
    if (action === 'role' && typeof payload.role === 'string') {
      const role: MeetingRole = payload.role === 'host' ? 'host' : 'audience';
      if (isLocalTarget) {
        this.set({ noticeKey: role === 'host' ? 'live.promoted' : null });
        this.patchParticipant(localUid, { role });
        return;
      }
      this.patchParticipant(targetUid, { role });
      return;
    }
    // Unhandled intents still refresh the roster for the moderator.
    this.patchParticipant(from, {});
  }

  private applyReaction(kind: LiveReactionKind, from: number, at: string): void {
    const reaction: ChatMessage = {
      id: `r_${at}_${from}_${kind}`,
      from,
      fromName: this.participant(from)?.displayName ?? this.names.get(from) ?? this.guestName(from),
      text: kind,
      at,
      kind: 'reaction',
    };
    this.set({
      reactions: { ...this.state.reactions, [kind]: (this.state.reactions[kind] ?? 0) + 1 },
      lastReaction: { kind, from, at },
      liveComments: [...this.state.liveComments, reaction].slice(-MAX_LIVE_COMMENTS),
      unreadLive: this.state.unreadLive + 1,
    });
  }

  private appendMessage(message: ChatMessage): void {
    const messages = [...this.state.messages, message].slice(-MAX_MESSAGES);
    const incoming = message.kind === 'user' && message.from !== (this.state.localUid ?? 0);
    const unread = incoming && !this.state.chatOpen ? this.state.unread + 1 : this.state.unread;
    this.set({ messages, unread });
    this.persistHistory(messages);
  }

  /** Writes the user messages of the current channel to the local history (system lines excluded). */
  private persistHistory(messages: readonly ChatMessage[]): void {
    const channel = this.state.channel;
    if (!channel) return;
    saveChatHistory(this.historyStorage, channel, messages);
  }

  /**
   * Forgets the local history of the current channel and drops the restored entries from the log.
   * The in-session messages received since joining stay; only the restored prefix is removed.
   */
  clearChatHistory(): void {
    const channel = this.state.channel;
    if (!channel) return;
    clearStoredChatHistory(this.historyStorage, channel);
    const localUid = this.state.localUid ?? 0;
    const kept = this.state.messages.filter(
      (message) => !message.restored && !(message.kind === 'reaction' && message.from === localUid),
    );
    this.restoredHistory = [];
    this.set({ messages: kept, historyRestored: 0 });
    this.appendMessage(systemMessage('system.historyCleared', {}, this.now()));
  }

  private appendLiveComment(comment: ChatMessage): void {
    const liveComments = [...this.state.liveComments, comment].slice(-MAX_LIVE_COMMENTS);
    const incoming = comment.kind === 'user' && comment.from !== (this.state.localUid ?? 0);
    this.set({
      liveComments,
      unreadLive: incoming ? this.state.unreadLive + 1 : this.state.unreadLive,
    });
  }

  private participant(uid: number): MeetingParticipant | undefined {
    return this.state.participants.find((entry) => entry.uid === uid);
  }

  private upsertParticipant(participant: MeetingParticipant): void {
    const existing = this.participant(participant.uid);
    const named = participant.displayName.length > 0;
    if (existing) {
      this.patchParticipant(participant.uid, participant);
      if (named && existing.displayName !== participant.displayName) {
        this.appendMessage(
          systemMessage('system.userJoined', { name: participant.displayName }, this.now()),
        );
      }
      return;
    }
    this.set({
      participants: [
        ...this.state.participants,
        { ...participant, displayName: participant.displayName || this.guestName(participant.uid) },
      ],
    });
    if (named) {
      this.appendMessage(
        systemMessage('system.userJoined', { name: participant.displayName }, this.now()),
      );
    }
  }

  private patchParticipant(uid: number, patch: Partial<MeetingParticipant>): void {
    this.set({
      participants: this.state.participants.map((entry) =>
        entry.uid === uid ? { ...entry, ...patch } : entry,
      ),
    });
  }
}

export class SceneCapacityError extends Error {
  constructor(readonly messageKey: MessageKey) {
    super(messageKey);
    this.name = 'SceneCapacityError';
  }
}

export function systemMessage(
  key: MessageKey,
  params: TranslateParams | undefined,
  at: number,
): ChatMessage {
  return {
    id: `s_${Math.random().toString(36).slice(2, 10)}`,
    from: 0,
    fromName: 'system',
    text: '',
    at: new Date(at).toISOString(),
    kind: 'system',
    textKey: key,
    ...(params ? { params } : {}),
  };
}

export function describeError(error: unknown): string {
  const rtc = error as Partial<RtcError> | undefined;
  if (rtc && typeof rtc.code === 'string')
    return `${rtc.code}${rtc.sdkCode !== undefined ? ` (${rtc.sdkCode})` : ''}: ${rtc.message ?? 'error'}`;
  return error instanceof Error ? error.message : String(error);
}

/** Event names the meeting relies on (kept explicit so a contract change fails loudly). */
export const REQUIRED_EVENTS: RtcEventName[] = [
  'connection-state-changed',
  'user-joined',
  'user-left',
  'user-mute-changed',
  'user-role-changed',
  'volume-indicator',
  'network-quality',
  'data-received',
  'token-privilege-will-expire',
  'token-privilege-did-expire',
];

/** Only hosts may moderate. */
export function canModerate(state: SessionState, uid: number): boolean {
  return state.participants.find((entry) => entry.uid === uid)?.role === 'host';
}
